import createAttachmentFromAttachmentDataProvider from './createAttachmentFromAttachmentDataProvider';
import createAttachmentFromLocalFile from './createAttachmentFromLocalFile';
import createAttachmentFromMailboxFile from './createAttachmentFromMailboxFile';
import createAttachmentFromUri from './createAttachmentFromUri';
import createReferenceAttachmentFromAttachmentDataProvider from './createReferenceAttachmentFromAttachmentDataProvider';
import createReferenceAttachmentFromLocalFile from './createReferenceAttachmentFromLocalFile';
import { uploadLocalFileAndCreateReferenceAttachment } from './uploadLocalFileAndCreateReferenceAttachment';
import AttachmentWellViewState from '../schema/AttachmentWellViewState';
import CreateAttachmentResult from '../types/CreateAttachmentResult';
import { ShouldShareAttachment } from '../types/CreateAttachmentsProperties';
import { assertNever } from 'owa-assert';
import { isFeatureEnabled } from 'owa-feature-flags';
import { AttachmentState } from 'owa-attachment-full-data';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ItemId from 'owa-service/lib/contract/ItemId';
import {
    CloudFile,
    LocalComputerFile,
    AttachmentFile,
    AttachmentFileType,
} from 'owa-attachment-file-types';
import createAttachmentFromLocalFileSliced, {
    CreateAttachmentStatus,
} from './createAttachmentFromLocalFileSliced';
import {
    onAttachmentCreationError,
    onAttachmentCreationSuccess,
} from './helpers/onAttachmentCreationHandlers';

export interface CreateAttachmentProcessResult {
    // createAttachmentPromise can either return AttachmentType if attachment was created otherwise null
    createAttachmentPromise: Promise<AttachmentType | null>;
    cancelCreateAttachment: () => void;
}

export interface CreateAttachmentDependencies {
    attachmentWellViewState: AttachmentWellViewState;
    parentItemId?: ItemId;
    infoBarAddHandler?: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void;
    infoBarRemoveHandler?: (infoBarMessageIds: string[]) => void;
}

export default function createAttachment(
    parentItemId: ClientItemId,
    attachment: AttachmentType,
    cancellationId: string,
    backingObject: AttachmentFile,
    attachmentState: AttachmentState,
    dependencies: CreateAttachmentDependencies,
    shouldShareAttachment: ShouldShareAttachment = { shouldShare: false }
): CreateAttachmentProcessResult {
    const isCloudFile: boolean =
        backingObject &&
        (backingObject.fileType === AttachmentFileType.Cloud ||
            backingObject.fileType === AttachmentFileType.CloudSuggestion ||
            backingObject.fileType === AttachmentFileType.ContextualSuggestion);
    let createServiceCommand: Promise<CreateAttachmentResult | null> = null;
    const attachmentId: ClientAttachmentId = {
        ...attachment.AttachmentId,
        mailboxInfo: parentItemId.mailboxInfo,
    };
    const { shouldShare } = shouldShareAttachment;

    let cancelCreateAttachment = () => {};

    if (isMailboxFile(backingObject)) {
        createServiceCommand = createAttachmentFromMailboxFile(
            parentItemId,
            attachment,
            cancellationId,
            backingObject,
            attachmentState,
            isCloudFile
        ).then(result => {
            return result;
        });
    } else if (!shouldShare && !isCloudFile) {
        if (
            backingObject &&
            backingObject.fileType === AttachmentFileType.Local &&
            backingObject.fileObject
        ) {
            // If the feature is enabled and is not a ItemId attachment type and the backing object is a local file
            // then create using slicing
            const { onCompletePromise, cancel } = createAttachmentFromLocalFileSliced(
                parentItemId,
                attachment,
                cancellationId,
                backingObject.fileObject,
                attachmentState
            );
            cancelCreateAttachment = cancel;

            createServiceCommand = onCompletePromise.then(result => {
                switch (result.status) {
                    case CreateAttachmentStatus.Completed:
                        return {
                            attachment: result.attachment,
                            sharingInfo: null,
                        };
                    case CreateAttachmentStatus.Cancelled:
                        // If the attachment is cancelled then return null
                        return null;
                    default:
                        return assertNever(result);
                }
            });
        } else if (backingObject && backingObject.fileType === AttachmentFileType.Uri) {
            const createAttachmentPromise = new Promise<AttachmentType>((resolve, reject) => {
                createAttachmentFromUri(
                    parentItemId,
                    attachmentId,
                    attachment,
                    cancellationId,
                    backingObject,
                    attachmentState,
                    resolve,
                    reject,
                    dependencies
                );
            });

            return {
                createAttachmentPromise: createAttachmentPromise,
                cancelCreateAttachment: cancelCreateAttachment,
            };
        } else {
            createServiceCommand = createAttachmentFromLocalFile(
                parentItemId,
                attachment,
                cancellationId,
                backingObject && backingObject.fileType === AttachmentFileType.Local
                    ? backingObject.fileObject
                    : null,
                attachmentState
            );
        }
    } else if (!shouldShare && isCloudFile) {
        // Attaching a cloud file
        const createAttachmentPromise = new Promise<AttachmentType>((resolve, reject) => {
            createAttachmentFromAttachmentDataProvider(
                parentItemId,
                attachmentId,
                attachment,
                cancellationId,
                backingObject as CloudFile,
                attachmentState,
                resolve,
                reject,
                dependencies
            );
        });

        return {
            createAttachmentPromise: createAttachmentPromise,
            cancelCreateAttachment: cancelCreateAttachment,
        };
    } else if (shouldShare && isCloudFile) {
        createServiceCommand = createReferenceAttachmentFromAttachmentDataProvider(
            parentItemId,
            cancellationId,
            backingObject as CloudFile,
            attachmentState
        );
    } else {
        // Sharing a local file
        if (
            isFeatureEnabled('doc-attachment-upsellEntryPoint') &&
            shouldShareAttachment.shouldShare &&
            shouldShareAttachment.shareProperties &&
            !shouldShareAttachment.shareProperties.groupMailboxId
        ) {
            const { onCompletePromise, cancel } = uploadLocalFileAndCreateReferenceAttachment(
                parentItemId,
                attachmentId,
                attachment,
                cancellationId,
                shouldShareAttachment.shareProperties.folderInfo,
                (backingObject as LocalComputerFile).fileObject,
                attachmentState,
                dependencies
            );

            return {
                createAttachmentPromise: onCompletePromise,
                cancelCreateAttachment: cancel,
            };
        } else {
            const createAttachmentPromise = new Promise<AttachmentType>((resolve, reject) => {
                createReferenceAttachmentFromLocalFile(
                    parentItemId,
                    attachmentId,
                    attachment,
                    cancellationId,
                    (backingObject as LocalComputerFile).fileObject,
                    attachmentState,
                    resolve,
                    reject,
                    dependencies
                );

                return attachment;
            });
            return {
                createAttachmentPromise: createAttachmentPromise,
                cancelCreateAttachment: cancelCreateAttachment,
            };
        }
    }

    return getCreateAttachmentProcessResult(
        parentItemId,
        attachmentId,
        attachment,
        attachmentState,
        dependencies,
        createServiceCommand,
        cancelCreateAttachment
    );
}

function getCreateAttachmentProcessResult(
    parentItemId: ClientItemId,
    attachmentId: ClientAttachmentId,
    attachment: AttachmentType,
    attachmentState: AttachmentState,
    dependencies: CreateAttachmentDependencies,
    createServiceCommand: Promise<CreateAttachmentResult | null>,
    cancelCreateAttachment: () => void
): CreateAttachmentProcessResult {
    const createAttachmentPromise = createServiceCommand
        .then((result: CreateAttachmentResult | null) => {
            if (result) {
                // If the result is not null then it means the attachment was created
                onAttachmentCreationSuccess(
                    attachmentState,
                    attachmentId,
                    attachment,
                    result.attachment,
                    parentItemId,
                    result.sharingInfo,
                    dependencies.attachmentWellViewState
                );

                return result.attachment;
            }

            return null;
        })
        .catch(error => {
            onAttachmentCreationError(
                attachmentState,
                dependencies.attachmentWellViewState,
                null,
                null
            );

            // Throw the error again so that the promise returned by this function can be caught
            throw error;
        });

    return {
        createAttachmentPromise: createAttachmentPromise,
        cancelCreateAttachment: cancelCreateAttachment,
    };
}

function isMailboxFile(file: AttachmentFile): boolean {
    return (
        file.fileType === AttachmentFileType.AttachmentItem ||
        file.fileType === AttachmentFileType.AttachmentItemSuggestion ||
        ((file.fileType === AttachmentFileType.Cloud ||
            file.fileType === AttachmentFileType.CloudSuggestion ||
            file.fileType === AttachmentFileType.ContextualSuggestion) &&
            file.providerType === AttachmentDataProviderType.Mailbox)
    );
}
